/**
 * Header renders the app header bar with room info, game controls, and utility buttons.
 *
 * Why: The header provides consistent navigation and controls across all app states.
 * It handles room menu, game-specific controls, theme selection, and debug panel toggle.
 *
 * When to use: Rendered by HeaderContainer which provides shellVM and focus navigation.
 * This is a large app component that receives VM and reads signals directly.
 */
import * as React from 'react'
import { useSignals } from '@preact/signals-react/runtime'
import { RoomMenu } from '../RoomMenu/RoomMenu'
import { GameControls } from '../GameControls/GameControls'
import { ThemeSelector } from '../ThemeSelector/ThemeSelector'
import { useTheme } from '../../../contexts/ThemeContext'
import { headerTestIds } from './header.testIds'
import type { HeaderItemType } from '../../../hooks/useHeaderFocusNavigation'
import type { ShellVMDep } from '../../../deps/ui/shellVM/shellVM.types'
import { ControllerIndicator } from './ControllerIndicator'
import { PauseButton } from './PauseButton'
import { GamelessToggleButton } from './GamelessToggleButton'
import { MerelsTitle } from './MerelsTitle'
import { ShareIconButton } from './ShareIconButton'
import { ShareDialog } from './ShareDialog'
import { LeaveRoomButton } from './LeaveRoomButton'
import { DebugPanelToggle } from './DebugPanelToggle'
import { ProfileAvatar } from './ProfileAvatar'
import { useTurnIndicatorModel } from '../../mateList/list/TurnIndicator/useTurnIndicatorModel'
import { TurnIndicatorLabel } from '../../mateList/list/TurnIndicator/TurnIndicator'
import type { ChallengeConfigurationControlProps } from './ChallengeConfigurationControl'
import { SafePlayerPiece } from '../../gameProvided/SafePlayerPiece'
import type { DevelopmentProgress } from '../../../deps/catalog/developmentProgress.types'

interface HeaderProps {
  shellVM: ShellVMDep
  focusedItem: HeaderItemType | null
  getRefCallback: (itemType: HeaderItemType) => (el: HTMLElement | null) => void
  challengeConfigurationControl?: React.ReactNode
  developmentProgress?: DevelopmentProgress
  onDevelopmentStatusClick?: () => void
}

export function Header({
  shellVM,
  focusedItem,
  getRefCallback,
  challengeConfigurationControl,
  developmentProgress,
  onDevelopmentStatusClick,
}: HeaderProps) {
  useSignals()

  const { getAppColor } = useTheme()
  const appColorShades = getAppColor()
  // Active seat owns header fill (backdrop) + accents (exact primary).
  const turnIndicator = useTurnIndicatorModel()
  const accentColor = turnIndicator?.accentColor ?? appColorShades.main
  const headerBackground = turnIndicator?.backdropColor ?? appColorShades.background
  const accentInkColor = turnIndicator?.accentInkColor ?? '#ffffff'

  // Read signal values
  const gameId = shellVM.game.gameId.value
  const isGameOver = shellVM.game.isGameOver.value
  const hasGameOptions = shellVM.game.hasGameOptions.value
  const hasTimers = shellVM.game.hasTimers.value
  const isGamePaused = shellVM.game.isGamePaused.value
  const ownerName = shellVM.room.ownerName.value
  const isOwner = shellVM.room.isOwner.value
  const imageUrl = shellVM.room.imageUrl.value
  const videoEnabled = shellVM.room.videoEnabled.value
  const isClosed = shellVM.room.isClosed.value
  const opensAt = shellVM.room.opensAt.value
  const isGameless = shellVM.room.gameless.value
  const roomName = shellVM.room.roomName.value
  const roomUrl = shellVM.room.roomUrl.value
  const shareDialogOpen = shellVM.shareDialog.isOpen.value
  const isHeaderFocused = shellVM.focus.isHeaderFocused.value
  const isFocusNavActive = shellVM.focus.isActive.value
  const debugSidebarOpen = shellVM.debugSidebarOpen.value

  // Match MateListPanel (160) + DebugPanelTabs (320) so the label centers on .game-area.
  const showsClosedView = isClosed
  const mateListWidthPx = isGameless || showsClosedView ? 0 : 160
  const debugSidebarWidthPx = debugSidebarOpen && !showsClosedView ? 320 : 0
  const playAreaCenterLeft = `calc(${mateListWidthPx}px + (100% - ${mateListWidthPx}px - ${debugSidebarWidthPx}px) / 2)`

  // Controller indicator
  const controllerIndicator = shellVM.controllerIndicator
  const isControllerIndicatorVisible = controllerIndicator.isVisible.value
  const controllerIndicatorTitle = controllerIndicator.title.value
  const controllerBadgeBgColor = controllerIndicator.badgeBgColor.value as 'red' | 'blue' | 'lime' | 'default'
  const controllerIconOpacity = controllerIndicator.iconOpacity.value as 'low' | 'full'

  const getTabIndex = (itemType: HeaderItemType) => {
    return isHeaderFocused && focusedItem === itemType ? 0 : -1
  }

  const isFocused = (itemType: HeaderItemType) => {
    return isHeaderFocused && focusedItem === itemType
  }

  const Piece = turnIndicator?.PlayerPiece ?? null
  const styledChallengeConfigurationControl = React.isValidElement<ChallengeConfigurationControlProps>(challengeConfigurationControl)
    ? React.cloneElement(challengeConfigurationControl, {
        accentColor,
        accentInkColor,
        backgroundColor: headerBackground,
      })
    : challengeConfigurationControl

  return (
    <header
      className="border-b"
      data-testid={headerTestIds.header}
      data-focus-navigation-active={isFocusNavActive}
      style={{
        ['--menu-bar-height' as string]: 'max(3vh, 48px)',
        ['--menu-btn-size' as string]: 'calc(var(--menu-bar-height) * 0.50)',
        ['--menu-btn-size-merels' as string]: 'calc(var(--menu-bar-height) * 0.8)',
        ['--menu-icon-size' as string]: '16px',
        ['--menu-title-size' as string]: 'calc(var(--menu-bar-height) * 0.8)',
        borderBottomColor: accentColor,
        borderBottomWidth: turnIndicator ? '10px' : '4px',
        borderBottomStyle: 'solid',
        backgroundColor: headerBackground
      }}
    >
      <div className="relative flex items-center px-6 h-[var(--menu-bar-height)]">
        <div className="mr-auto flex items-center gap-4 min-w-0">
          <RoomMenu
            roomTitle={roomName || 'Room'}
            ownerName={ownerName}
            isOwner={isOwner}
            imageUrl={imageUrl}
            videoEnabled={videoEnabled}
            isClosed={isClosed}
            opensAt={opensAt}
            managed={shellVM.room.managed.value}
            onSetManaged={shellVM.actions.setManagedMode}
            hostTerm={shellVM.room.hostTerm.value}
            onSetHostTerm={shellVM.actions.setHostTerm}
            curated={shellVM.room.curated.value}
            onSetCurated={shellVM.actions.setCuratedMode}
            onUpdateRoom={shellVM.actions.updateRoom}
            onCopyLink={shellVM.actions.copyRoomLink}
            onLeaveRoom={shellVM.actions.leaveRoom}
            triggerRef={getRefCallback('room-menu')}
            tabIndex={getTabIndex('room-menu')}
            isFocused={isFocused('room-menu')}
            accentColor={accentColor}
            accentInkColor={accentInkColor}
            backgroundColor={headerBackground}
          />
          {gameId ? (
            <GameControls
                shellVM={shellVM}
                developmentProgress={developmentProgress}
                onDevelopmentStatusClick={onDevelopmentStatusClick}
                accentColor={accentColor}
                accentInkColor={accentInkColor}
                backgroundColor={headerBackground}
                hintsButtonRef={shellVM.game.hasHints.value ? getRefCallback('game-hints') : undefined}
                rulebookButtonRef={shellVM.game.hasRulebook.value ? getRefCallback('game-rulebook') : undefined}
                infoButtonRef={getRefCallback('game-info')}
                configureButtonRef={hasGameOptions ? getRefCallback('game-configure') : undefined}
                closeButtonRef={getRefCallback('game-close')}
                playAgainButtonRef={getRefCallback('play-again')}
                focusedItem={
                  focusedItem === 'game-hints' ? 'hints' :
                  focusedItem === 'game-rulebook' ? 'rulebook' :
                  focusedItem === 'game-info' ? 'info' :
                  focusedItem === 'game-configure' ? 'configure' :
                  focusedItem === 'game-close' ? 'close' :
                  focusedItem === 'play-again' ? 'play-again' :
                  null
                }
              challengeConfigurationControl={styledChallengeConfigurationControl}
            />
          ) : (
            <MerelsTitle accentColor={accentColor} />
          )}
        </div>
        {turnIndicator && (
          <div
            className="pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[40%] truncate"
            style={{ left: playAreaCenterLeft }}
          >
            <TurnIndicatorLabel
              text={turnIndicator.text}
              textColor={turnIndicator.textColor}
              piece={
                Piece && turnIndicator.playerIndex >= 0 ? (
                  <SafePlayerPiece
                    component={Piece}
                    playerIndex={turnIndicator.playerIndex}
                    playerId={turnIndicator.playerId}
                    size={18}
                  />
                ) : null
              }
              dataAttrs={turnIndicator.dataAttrs}
            />
          </div>
        )}
        <div className="flex items-center gap-2 shrink-0">
          {isControllerIndicatorVisible && (
            <ControllerIndicator
              title={controllerIndicatorTitle}
              badgeBgColor={controllerBadgeBgColor}
              iconOpacity={controllerIconOpacity}
            />
          )}
          {hasTimers && !isGameOver && (
            <PauseButton
              isPaused={isGamePaused}
              onToggle={shellVM.actions.togglePause}
              buttonRef={getRefCallback('pause-button')}
              tabIndex={getTabIndex('pause-button')}
              isFocused={isFocused('pause-button')}
              accentColor={accentColor}
              accentInkColor={accentInkColor}
              backgroundColor={headerBackground}
            />
          )}
          <GamelessToggleButton
            isGameless={isGameless}
            onToggle={shellVM.actions.toggleGameless}
            buttonRef={getRefCallback('gameless-button')}
            tabIndex={getTabIndex('gameless-button')}
            isFocused={isFocused('gameless-button')}
            accentColor={accentColor}
            accentInkColor={accentInkColor}
            backgroundColor={headerBackground}
          />
          <ShareDialog
            open={shareDialogOpen}
            onOpenChange={shellVM.shareDialog.setIsOpen}
            roomUrl={roomUrl}
            onCopyLink={shellVM.actions.copyRoomLink}
            trigger={
              <ShareIconButton
                buttonRef={getRefCallback('share-button')}
                tabIndex={getTabIndex('share-button')}
                isFocused={isFocused('share-button')}
                accentColor={accentColor}
                accentInkColor={accentInkColor}
                backgroundColor={headerBackground}
              />
            }
          />
          <LeaveRoomButton
            onLeave={shellVM.actions.leaveRoom}
            buttonRef={getRefCallback('leave-button')}
            tabIndex={getTabIndex('leave-button')}
            isFocused={isFocused('leave-button')}
            accentColor={accentColor}
            accentInkColor={accentInkColor}
            backgroundColor={headerBackground}
          />
          <ThemeSelector
            buttonRef={getRefCallback('theme-selector')}
            tabIndex={getTabIndex('theme-selector')}
            isFocused={isFocused('theme-selector')}
            accentColor={accentColor}
            accentInkColor={accentInkColor}
            backgroundColor={headerBackground}
          />
          <DebugPanelToggle
            isOpen={debugSidebarOpen}
            onToggle={shellVM.actions.toggleDebugSidebar}
            isOwnedGame={shellVM.isOwnedGame.value}
            buttonRef={getRefCallback('debug-toggle')}
            tabIndex={getTabIndex('debug-toggle')}
            isFocused={isFocused('debug-toggle')}
            accentColor={accentColor}
            accentInkColor={accentInkColor}
            backgroundColor={headerBackground}
          />
          <ProfileAvatar
            shellVM={shellVM}
            buttonRef={getRefCallback('profile-avatar')}
            tabIndex={getTabIndex('profile-avatar')}
            isFocused={isFocused('profile-avatar')}
            accentColor={accentColor}
            backgroundColor={headerBackground}
          />
        </div>
      </div>
    </header>
  )
}
